import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Modal } from 'antd';
import chatStore from '../stores/chatStore';
import { useTheme } from '../theme/ThemeContext';
import NewChatButton from '../chat/NewChatButton';
import CreditsBalance from '../chat/CreditsBalance';
import ChatSearch from '../chat/ChatSearch';
import ChatList from '../chat/ChatList';
import GptSettingsPanel from '../settings/GptSettingsPanel';

/** Обёртка Modal: перетаскивание за заголовок */
const DraggableModalRender = ({ children, offset, setOffset }) => {
    const wrapRef = useRef(null);
    const dragRef = useRef(null);

    const onMouseDown = (e) => {
        const header = wrapRef.current?.querySelector?.('.ant-modal-header');
        if (!header || !header.contains(e.target)) return;
        if (e.target.closest?.('.ant-modal-close')) return;
        e.preventDefault();
        dragRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            originX: offset.x,
            originY: offset.y,
        };
        const onMove = (ev) => {
            if (!dragRef.current) return;
            setOffset({
                x: dragRef.current.originX + (ev.clientX - dragRef.current.startX),
                y: dragRef.current.originY + (ev.clientY - dragRef.current.startY),
            });
        };
        const onUp = () => {
            dragRef.current = null;
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };

    return (
        <div
            ref={wrapRef}
            onMouseDown={onMouseDown}
            style={{ transform: `translate(${offset.x}px, ${offset.y}px)` }}
        >
            {children}
        </div>
    );
};

const ChatSidebar = observer(() => {
    const { theme } = useTheme();
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [settingsOffset, setSettingsOffset] = useState({ x: 0, y: 0 });
    const [hoverExpand, setHoverExpand] = useState(false);
    const isDark = theme === 'dark';
    const collapsed = chatStore.sidebarCollapsed;
    const showFull = !collapsed || hoverExpand;

    useEffect(() => {
        if (settingsOpen) setSettingsOffset({ x: 0, y: 0 });
    }, [settingsOpen]);

    useEffect(() => {
        if (!collapsed) setHoverExpand(false);
    }, [collapsed]);

    const closeSettings = () => {
        setSettingsOpen(false);
        setHoverExpand(false);
    };

    const panelClass = `flex h-full flex-col border-r ${
        isDark ? 'border-white/10 bg-[#171717]' : 'border-black/10 bg-[#f0f0f0]'
    }`;

    const settingsModal = (
        <Modal
            title={
                <div style={{ cursor: 'move', userSelect: 'none' }}>
                    Настройки {chatStore.providerLabel}
                </div>
            }
            open={settingsOpen}
            onCancel={closeSettings}
            footer={null}
            destroyOnClose
            modalRender={(node) => (
                <DraggableModalRender offset={settingsOffset} setOffset={setSettingsOffset}>
                    {node}
                </DraggableModalRender>
            )}
        >
            <GptSettingsPanel onClose={closeSettings} />
        </Modal>
    );

    const fullBody = (
        <>
            <div className="flex flex-col gap-0 p-3 pb-0">
                <CreditsBalance />
            </div>
            <div className="flex items-center justify-between gap-2 p-3 pt-2">
                <NewChatButton />
                <button
                    type="button"
                    className={`rounded-md p-1.5 ${
                        chatStore.sidebarPinned
                            ? isDark
                                ? 'bg-white/15 text-white'
                                : 'bg-black/10 text-black'
                            : isDark
                              ? 'text-white/70 hover:bg-white/10 hover:text-white'
                              : 'text-black/55 hover:bg-black/5 hover:text-black'
                    }`}
                    onClick={() => chatStore.toggleSidebarPinned()}
                    title={chatStore.sidebarPinned ? 'Открепить меню' : 'Закрепить меню'}
                    aria-pressed={chatStore.sidebarPinned}
                    aria-label={chatStore.sidebarPinned ? 'Открепить меню' : 'Закрепить меню'}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                        {chatStore.sidebarPinned ? (
                            <path
                                d="M16 3l5 5-4 1-4 4v4l-3-3-5 5-1-1 5-5-3-3h4l4-4 1-4z"
                                fill="currentColor"
                            />
                        ) : (
                            <path
                                d="M15 4.5l4.5 4.5-3.2.9-3.8 3.8v3.3l-2.5-2.5-4.7 4.7-.7-.7 4.7-4.7-2.5-2.5h3.3l3.8-3.8.9-3.2z"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinejoin="round"
                            />
                        )}
                    </svg>
                </button>
                <button
                    type="button"
                    className={`rounded-md px-2 py-1 text-sm ${isDark ? 'hover:bg-white/10' : 'hover:bg-black/5'}`}
                    onClick={() => {
                        if (chatStore.sidebarPinned) chatStore.setSidebarPinned(false);
                        setHoverExpand(false);
                        chatStore.setSidebarCollapsed(true);
                    }}
                    title={chatStore.sidebarPinned ? 'Открепить и свернуть' : 'Свернуть'}
                >
                    «
                </button>
            </div>
            <div className="px-3 pb-2">
                <ChatSearch />
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
                <ChatList />
            </div>
            <div className={`border-t p-3 ${isDark ? 'border-white/10' : 'border-black/10'}`}>
                <button
                    type="button"
                    className={`w-full rounded-lg px-3 py-2 text-left text-sm ${
                        isDark ? 'hover:bg-white/10' : 'hover:bg-black/5'
                    }`}
                    onClick={() => setSettingsOpen(true)}
                >
                    Настройки
                </button>
            </div>
            {settingsModal}
        </>
    );

    if (collapsed) {
        return (
            <div
                className="relative z-20 w-12 shrink-0"
                onMouseEnter={() => setHoverExpand(true)}
                onMouseLeave={() => {
                    if (!settingsOpen) setHoverExpand(false);
                }}
            >
                <aside
                    className={`${panelClass} absolute inset-y-0 left-0 shadow-xl transition-[width] duration-200 ease-out ${
                        showFull ? 'w-[260px]' : 'w-12'
                    }`}
                >
                    {showFull ? (
                        fullBody
                    ) : (
                        <div className="flex flex-col items-center gap-3 py-3">
                            <button
                                type="button"
                                className={`rounded-md p-2 ${isDark ? 'hover:bg-white/10' : 'hover:bg-black/5'}`}
                                onClick={() => chatStore.setSidebarCollapsed(false)}
                                title="Развернуть"
                            >
                                »
                            </button>
                            <CreditsBalance compact />
                            <NewChatButton compact />
                        </div>
                    )}
                </aside>
            </div>
        );
    }

    return <aside className={`${panelClass} w-[260px] shrink-0`}>{fullBody}</aside>;
});

export default ChatSidebar;
